import { Component, inject, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AggregatedSlot, BookingRequest } from '../../models/public-calendar.models';
import { PublicI18nService } from '../../services/public-i18n.service';

@Component({
  selector: 'pc-booking-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  styles: [`
    :host {
      display: block;
    }
    .form-container {
      border-radius: 0.75rem;
      border: 1px solid var(--color-border, #e2e8f0);
      background-color: var(--color-surface, #fff);
      padding: 1.5rem;
    }
    .form-title {
      font-size: 1.25rem;
      font-weight: 600;
      margin-bottom: 1.5rem;
      color: var(--color-foreground, #1e293b);
    }
    .slot-summary {
      margin-bottom: 1.5rem;
      padding: 1rem;
      border-radius: 0.5rem;
      background-color: rgba(0, 102, 255, 0.08);
      border: 1px solid rgba(0, 102, 255, 0.2);
    }
    .slot-label {
      font-size: 0.875rem;
      color: var(--color-muted, #64748b);
      margin-bottom: 0.25rem;
    }
    .slot-value {
      font-weight: 600;
      color: var(--color-foreground, #1e293b);
    }
    .change-slot-btn {
      font-size: 0.875rem;
      color: var(--color-primary, #0066ff);
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      margin-top: 0.25rem;
    }
    .change-slot-btn:hover {
      text-decoration: underline;
    }
    .form-group {
      margin-bottom: 1rem;
    }
    .form-group-lg {
      margin-bottom: 1.5rem;
    }
    .form-label {
      display: block;
      font-size: 0.875rem;
      font-weight: 500;
      margin-bottom: 0.25rem;
      color: var(--color-foreground, #1e293b);
    }
    .form-input {
      width: 100%;
      padding: 0.625rem 0.875rem;
      font-size: 0.875rem;
      border-radius: 0.5rem;
      border: 1px solid var(--color-border, #e2e8f0);
      background-color: var(--color-surface, #fff);
      color: var(--color-foreground, #1e293b);
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
      outline: none;
    }
    .form-input::placeholder {
      color: var(--color-muted, #94a3b8);
    }
    .form-input:focus {
      border-color: var(--color-primary, #0066ff);
      box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
    }
    .form-input.ng-invalid.ng-touched {
      border-color: #ef4444;
    }
    .form-error {
      font-size: 0.875rem;
      color: #ef4444;
      margin-top: 0.25rem;
    }
    .error-alert {
      margin-bottom: 1rem;
      padding: 0.75rem;
      border-radius: 0.5rem;
      background-color: #fef2f2;
      border: 1px solid #fecaca;
      color: #dc2626;
      font-size: 0.875rem;
    }
    .submit-btn {
      width: 100%;
      padding: 0.875rem 1.5rem;
      font-size: 1rem;
      font-weight: 600;
      border-radius: 0.5rem;
      border: none;
      background-color: var(--color-primary, #0066ff);
      color: #fff;
      cursor: pointer;
      transition: background-color 0.15s ease, transform 0.1s ease;
    }
    .submit-btn:hover:not(:disabled) {
      filter: brightness(1.1);
    }
    .submit-btn:active:not(:disabled) {
      transform: scale(0.98);
    }
    .submit-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
    .consent-text {
      font-size: 0.75rem;
      color: var(--color-muted, #64748b);
      text-align: center;
      margin-top: 1rem;
    }
    .spinner {
      display: inline-block;
      width: 1.25rem;
      height: 1.25rem;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top-color: #fff;
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `],
  template: `
    <div class="form-container">
      <h2 class="form-title">{{ i18n.t('form.contactData') }}</h2>

      <!-- Selected slot summary -->
      @if (selectedSlot()) {
        <div class="slot-summary">
          <p class="slot-label">{{ i18n.t('form.selectedSlot') }}</p>
          <p class="slot-value">
            {{ formatDate(selectedSlot()!.date) }} {{ i18n.t('form.at') }} {{ i18n.formatTime(selectedSlot()!.startTime) }} - {{ i18n.formatTime(selectedSlot()!.endTime) }}
          </p>
          <button
            type="button"
            class="change-slot-btn"
            (click)="changeSlot.emit()"
          >
            {{ i18n.t('form.changeSlot') }}
          </button>
        </div>
      }

      <form [formGroup]="form" (ngSubmit)="onSubmit()">
        <!-- Name -->
        <div class="form-group">
          <label for="name" class="form-label">
            {{ i18n.t('form.name') }} <span style="color: #ef4444;">*</span>
          </label>
          <input
            id="name"
            type="text"
            formControlName="customerName"
            class="form-input"
            [placeholder]="i18n.t('form.namePlaceholder')"
          />
          @if (form.get('customerName')?.touched && form.get('customerName')?.errors?.['required']) {
            <p class="form-error">{{ i18n.t('form.nameRequired') }}</p>
          }
        </div>

        <!-- Email -->
        <div class="form-group">
          <label for="email" class="form-label">
            {{ i18n.t('form.email') }} <span style="color: #ef4444;">*</span>
          </label>
          <input
            id="email"
            type="email"
            formControlName="customerEmail"
            class="form-input"
            [placeholder]="i18n.t('form.emailPlaceholder')"
          />
          @if (form.get('customerEmail')?.touched && form.get('customerEmail')?.errors?.['required']) {
            <p class="form-error">{{ i18n.t('form.emailRequired') }}</p>
          }
          @if (form.get('customerEmail')?.touched && form.get('customerEmail')?.errors?.['email']) {
            <p class="form-error">{{ i18n.t('form.emailInvalid') }}</p>
          }
        </div>

        <!-- Phone -->
        <div class="form-group">
          <label for="phone" class="form-label">
            {{ i18n.t('form.phone') }}
          </label>
          <input
            id="phone"
            type="tel"
            formControlName="customerPhone"
            class="form-input"
            placeholder="+49 123 456789"
          />
        </div>

        <!-- Notes -->
        <div class="form-group-lg">
          <label for="notes" class="form-label">
            {{ i18n.t('form.message') }}
          </label>
          <textarea
            id="notes"
            formControlName="notes"
            rows="3"
            class="form-input"
            style="resize: none;"
            [placeholder]="i18n.t('form.messagePlaceholder')"
          ></textarea>
        </div>

        <!-- Error message -->
        @if (errorMessage()) {
          <div class="error-alert">
            {{ errorMessage() }}
          </div>
        }

        <!-- Submit button -->
        <button
          type="submit"
          class="submit-btn"
          [disabled]="!form.valid || submitting()"
          (click)="onSubmit()"
        >
          @if (submitting()) {
            <span style="display: flex; align-items: center; justify-content: center; gap: 0.5rem;">
              <span class="spinner"></span>
              {{ i18n.t('form.submitting') }}
            </span>
          } @else {
            {{ i18n.t('form.submit') }}
          }
        </button>

        <p class="consent-text">
          {{ i18n.t('form.consent') }}
        </p>
      </form>
    </div>
  `,
})
export class BookingFormComponent {
  readonly i18n = inject(PublicI18nService);

  selectedSlot = input.required<AggregatedSlot>();
  submitting = input<boolean>(false);
  errorMessage = input<string | null>(null);

  submitRequest = output<BookingRequest>();
  changeSlot = output<void>();

  form: FormGroup;

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      customerName: ['', [Validators.required, Validators.minLength(2)]],
      customerEmail: ['', [Validators.required, Validators.email]],
      customerPhone: [''],
      notes: [''],
    });
  }

  formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString(this.i18n.getDateLocale(), {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const slot = this.selectedSlot();
    const formValue = this.form.value;

    const request: BookingRequest = {
      date: slot.date,
      startTime: slot.startTime,
      endTime: slot.endTime,
      customerName: formValue.customerName.trim(),
      customerEmail: formValue.customerEmail.trim(),
      customerPhone: formValue.customerPhone?.trim() || undefined,
      notes: formValue.notes?.trim() || undefined,
      language: this.i18n.locale(),
    };

    this.submitRequest.emit(request);
  }
}
